import {createModal} from "../../../../../helpers/modal";
import {createGrid} from "../../../../../helpers/grids/grids";
import {CustomSelectCellEditor} from "../cellEditors/customSelectCellEditor";
import {GanttWrapper, getConstraintTypeDisplayName} from "../ganttWrapper";
import {createCellRenderer} from "../../../../../helpers/grids/helpers";
import createElement from "../../../../../helpers/createElement";
import {nl2br} from "../../../../../helpers/nl2br";

/** @type {TaskConstraintsModal.Modal | undefined} */
let taskModalInstance = undefined;

// noinspection JSUnusedGlobalSymbols imported via modal.d.ts,JSValidateTypes
/**
 * @param {GanttWrapper.GanttWrapper} ganttInstance
 * @return {TaskConstraintsModal.Modal}
 */
function createTaskConstraintsModal(ganttInstance) {
	/**
	 * @type {GanttWrapper.Constraint[]}
	 */
	const gridData = [];
	/**
	 * @type {GanttWrapper.Task}
	 */

	if (taskModalInstance === undefined) {
		// noinspection JSValidateTypes
		taskModalInstance = createModal({
			modalContainer: document.querySelector('.timeline-tasks-constraints-modal'), // There should be only one instance of this modal ever
			/** @type {import('../../../../../helpers/grids/grids').GridInstance} Defined on init */
			grid: null,
			currentTask: null,
			ganttInstance,
			init() {
				// Init grid
				this.grid = createGrid(
					this.modalContainer.querySelector('.grid-container'),
					{
						pagination: false,
						popupParent: this.modalContainer,
						enableCellTextSelection: false,
						paginationPageSize: 1500,
						cacheBlockSize: 2000,
						onCellEditingStopped: handleCellEdit,
						columnDefs: [
							{
								headerName: 'Type',
								field: 'type',
								cellEditor: CustomSelectCellEditor,
								cellRenderer: createCellRenderer(params => {
									return createElement(`<div>${getConstraintTypeDisplayName(params.value)}</div>`)
								}),
								cellEditorParams: {
									select2Options: {},
									getAvailableOptions: () => {
										return [
											{
												value: 'starts-with',
												text: getConstraintTypeDisplayName('starts-with'),
											},
											{
												value: 'starts-after',
												text: getConstraintTypeDisplayName('starts-after'),
											},
											{
												value: 'ends-with',
												text: getConstraintTypeDisplayName('ends-with'),
											},
											{
												value: 'ends-before',
												text: getConstraintTypeDisplayName('ends-before'),
											}
										];
									}
								},
								editable: true,
							},
							{
								headerName: 'Task',
								field: 'target_task_id',
								cellEditor: CustomSelectCellEditor,
								cellEditorParams: {
									getAvailableOptions: (constraint) => {
										return getAvailableTasksForConstraint(constraint).map(task => {
											return {
												value: task.id,
												text: task.name
											}
										})
									},
								},
								cellRenderer: createCellRenderer(params => {
									if (params.value) {
										// Retrieve task name from Id
										const text = ganttInstance._findTaskById(params.data.target_task_id).name;

										return createElement(`<div>${text}</div>`)

									} else {
										const text = getAvailableTasksForConstraint(params.data).length > 0 ?
											'Select A Task' : 'No Task Available';

										return createElement(`<div class="text-muted">${text}</div>`)
									}

								}),
								editable: params => {
									// Can only edit if there are task to select
									return getAvailableTasksForConstraint(params.data).length > 0;
								},
							},
							{
								headerName: 'Days Offset',
								field: 'days_offset',
								cellEditor: 'agNumberCellEditor',
								cellEditorParams: {
									precision: 0,
								},
								editable: true,
							},
							{
								headerName: '',
								field: 'delete',
								cellRenderer: renderDeleteCell,
							}
						]
					},
					gridData,
				);

				// Init confirm button
				this.modalContainer
					.querySelector('.timeline-tasks-constraints-modal-confirm-button')
					.addEventListener('click', saveConstraints);
			},
			setTask,
		})

	}

	return taskModalInstance;

	/** Methods */

	function refreshGrid() {
		/** @type {import('../../../../../helpers/grids/grids').GridInstance} Defined on init */
		const gridInstance = taskModalInstance.grid;

		gridInstance._refresh();
	}

	/**
	 * @param {import('ag-grid-community/dist/lib/events').CellEditRequestEvent} event
	 */
	function handleCellEdit(event) {
		if (event.column.getColId() === 'target_task_id' && event.oldValue === '') {
			// If we just added a value to an empty
			// row create a new empty row at the end
			addEmptyRow();

			refreshGrid();
		}

		renderConstraintDescriptions();
	}

	/**
	 * @param {import('../ganttWrapper').GanttWrapper.Constraint} constraint
	 * @return {import('../ganttWrapper').GanttWrapper.Task[]}
	 */
	function getAvailableTasksForConstraint(constraint) {
		if (!constraint) {
			// In case the grid is still setting up stuff
			return [];
		}

		const availableTasks = [];

		const parentsIds = ganttInstance
			._getTaskParents(taskModalInstance.currentTask)
			.map(task => task.id);

		ganttInstance._getTasks().forEach(task => {
			// Skip the task we are editing the constraint of
			if (task.id === taskModalInstance.currentTask.id) {
				return;
			}

			// And its parents
			if (parentsIds.includes(task.id)) {
				return
			}

			let available = true;
			for (const _constraint of gridData) {
				// Check if is not already in use by any other constraint
				if (_constraint.target_task_id === task.id && _constraint !== constraint) {
					available = false;
					break;
				}
			}

			if (available) {
				availableTasks.push(task);
			}
		})

		return availableTasks;
	}

	// noinspection JSCheckFunctionSignatures
	/**
	 * @param {import('../ganttWrapper').GanttWrapper.Task} task
	 */
	function setTask(task) {
		// Save in modal instance
		taskModalInstance.currentTask = task;

		// Update title
		taskModalInstance.modalContainer.querySelector('.modal-title').innerText = `'${task.name}' Constraints`;

		// Update grid data/constraints

		// Clear all rows
		gridData.length = 0;

		// Add rows from task
		taskModalInstance.currentTask.constraints.forEach(existingConstraint => {
			gridData.push(existingConstraint);
		})

		// Add empty row
		addEmptyRow();

		refreshGrid();

		renderConstraintDescriptions();
	}

	function addEmptyRow() {
		// noinspection JSCheckFunctionSignatures -- We can ignore the invalid type
		gridData.push({
			target_task_id: '',
			type: 'starts-after',
			days_offset: 0,
		})
	}

	function renderDeleteCell(params) {
		if (params.data.target_task_id === '') {
			// Prevent deletion of empty row
			return createElement('<div></div>');
		}

		const button = createElement(
			`
			<div class="h-100 d-flex align-items-center justify-content-center">
				<button type="button" class="btn btn-danger" style="font-size: 1.2rem">
					<span class="mdi mdi-delete"></span>
				</button>
			</div>
			`
		);

		button.addEventListener('click', () => {
			// Get index to remove
			let constraintIndex = undefined;

			for (const constraint of gridData) {
				const index = gridData.indexOf(constraint);
				if (constraint.target_task_id === params.data.target_task_id) {
					constraintIndex = index;
					break;
				}
			}

			if (constraintIndex !== undefined) {
				gridData.splice(constraintIndex, 1);
			}

			refreshGrid();
			renderConstraintDescriptions();
		})

		return button;
	}

	function saveConstraints() {
		//TODO: Check for constraint loops

		// Save all but the last row to the task
		taskModalInstance.currentTask.constraints = gridData.slice(0, -1);

		taskModalInstance.notifySuccessListeners(taskModalInstance.currentTask.constraints);

		taskModalInstance.hide();
	}

	function renderConstraintDescriptions() {
		if (gridData.length <= 1) {
			// No constraints
			taskModalInstance.modalContainer
				.querySelector('.constraints-description')
				.innerHTML = '<span class="">No Constraints Set</span>';

			return;
		}

		// Make copy of current constraints
		const ogConstraints = taskModalInstance.currentTask.constraints;

		// Save Constraints
		taskModalInstance.currentTask.constraints = gridData.slice(0, -1);

		// Get description
		// noinspection UnnecessaryLocalVariableJS
		const description = taskModalInstance.ganttInstance
			._getTaskConstraintDescription(
				taskModalInstance.currentTask,
				true
			);

		// Update on DOM
		taskModalInstance.modalContainer
			.querySelector('.constraints-description')
			.innerHTML = nl2br(description);

		// Restore original constraints
		taskModalInstance.currentTask.constraints = ogConstraints;
	}
}

export {createTaskConstraintsModal}
